import { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react'
import { Toaster } from 'sonner'
import { usePostHog } from 'posthog-js/react'
import { useApiAuth } from './auth/useApiAuth'
import { usePostHogIdentify } from './analytics/usePostHogIdentify'
import Layout from './components/Layout'
import { AdminRoute } from './components/AdminRoute'
import HomePage from './pages/HomePage'
import DashboardPage from './pages/DashboardPage'
import ExpensesPage from './pages/ExpensesPage'
import DocumentsPage from './pages/DocumentsPage'
import ExportPage from './pages/ExportPage'
import AccountsPage from './pages/AccountsPage'
import SettingsPage from './pages/SettingsPage'
import AdminLayout from './pages/admin/AdminLayout'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import CategoriesPage from './pages/admin/CategoriesPage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import NotFoundPage from './pages/NotFoundPage'
import SecurityPage from './pages/SecurityPage'
import ImportPage from './pages/ImportPage'
import OnboardingPage from './pages/OnboardingPage'
import BlogIndexPage from './pages/BlogIndexPage'
import BlogPostPage from './pages/BlogPostPage'
import { useOnboarding } from './hooks/useOnboarding'
import { Navigate } from 'react-router-dom'

function App() {
  // Initialize API client with Clerk authentication
  useApiAuth()
  // Sync Clerk identity to PostHog
  usePostHogIdentify()

  // Track pageviews on every route change
  const location = useLocation()
  const posthog = usePostHog()
  useEffect(() => {
    posthog.capture('$pageview', { $current_url: window.location.href })
  }, [location, posthog])

  return (
    <>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/security" element={<SecurityPage />} />
        {/* Onboarding — full-page, no Layout */}
        <Route
          path="/onboarding"
          element={
            <>
              <SignedIn>
                <OnboardingPage />
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <>
              <SignedIn>
                <OnboardingGuard>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </OnboardingGuard>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/expenses/import"
          element={
            <>
              <SignedIn>
                <Layout>
                  <ImportPage />
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/expenses"
          element={
            <>
              <SignedIn>
                <Layout>
                  <ExpensesPage />
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/documents"
          element={
            <>
              <SignedIn>
                <Layout>
                  <DocumentsPage />
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/export"
          element={
            <>
              <SignedIn>
                <Layout>
                  <ExportPage />
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/accounts"
          element={
            <>
              <SignedIn>
                <Layout>
                  <AccountsPage />
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/settings"
          element={
            <>
              <SignedIn>
                <Layout>
                  <SettingsPage />
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <>
              <SignedIn>
                <Layout>
                  <AdminRoute>
                    <AdminLayout>
                      <AdminDashboardPage />
                    </AdminLayout>
                  </AdminRoute>
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <>
              <SignedIn>
                <Layout>
                  <AdminRoute>
                    <AdminLayout>
                      <CategoriesPage />
                    </AdminLayout>
                  </AdminRoute>
                </Layout>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        {/* Blog routes — public */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  )
}

export default App

/**
 * Redirects first-time users to /onboarding before they reach the dashboard.
 * Skipped users (onboardingDismissed = true) pass through immediately.
 */
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { isLoading, shouldShowWizard } = useOnboarding()

  // While loading profile, render nothing to avoid flash
  if (isLoading) return null

  if (shouldShowWizard) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}
